import { useNavigate, useParams } from "react-router-dom";
import AdminOnly from "#/components/auth/AdminOnly";
import Typography from "#/components/shared/typography";
import { ActionButton } from "#/components/shared/buttons/ActionButtons";
import DetailSection from "#/components/shared/details/DetailSection";
import InfoRowLongText from "#/components/shared/InfoRowLongText";
import { formatDate } from "#/components/utls/time";
import useArticleReviewDetail from "#/admin/article-reviews/hooks/useArticleReviewDetail";
import useArticleReviewsList from "#/admin/article-reviews/hooks/useArticleReviewsList";
import { ArticleReviewList } from "#/admin/article-reviews/components/ArticleReviewList";

export default function AdminArticleReviewDetailPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const detailQuery = useArticleReviewDetail(id);
  const articleId = detailQuery.data?.article_id;
  const listQuery = useArticleReviewsList(
    { articleId: articleId ?? undefined },
    { enabled: Boolean(articleId) }
  );

  if (!id) {
    return (
      <AdminOnly>
        <div className="container mx-auto px-4 py-6">
          <Typography variant="h4" className="text-terracotta">
            Review not found
          </Typography>
        </div>
      </AdminOnly>
    );
  }

  return (
    <AdminOnly>
      <div className="container mx-auto flex flex-col gap-6 px-4 py-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-col gap-1">
            <Typography variant="caption" className="text-brown-500">
              Article reviews / {detailQuery.data?.id ?? id}
            </Typography>
            <Typography variant="h2" className="text-brown-900">
              Review details
            </Typography>
          </div>
          <ActionButton
            tone="secondary"
            onClick={() => navigate("/admin/content?tab=reviews")}
          >
            Back to reviews
          </ActionButton>
        </div>

        {detailQuery.isLoading ? (
          <Typography variant="body2" className="text-brown-600">
            Loading review…
          </Typography>
        ) : detailQuery.isError ? (
          <Typography variant="body2" className="text-terracotta">
            {detailQuery.error instanceof Error
              ? detailQuery.error.message
              : "Unable to load review."}
          </Typography>
        ) : detailQuery.data ? (
          <div className="flex flex-col gap-6">
            <DetailSection
              title="Review notes"
              description="Content of the review."
              contentClassName="space-y-6"
            >
              <InfoRowLongText
                label="Review notes"
                text={detailQuery.data.notes ?? "No notes for this review."}
                accent="beige"
                initiallyExpanded
                previewCharacterCount={800}
                className="border border-beige-200 bg-white shadow-md"
              />
              <div className="flex flex-wrap items-center gap-3 text-xs text-brown-600">
                <span className="rounded-full bg-cream-100 px-3 py-1 font-semibold text-brown-700">
                  {detailQuery.data.status || "Unknown"}
                </span>
                <span>Review ID: {detailQuery.data.id}</span>
                <span>Article ID: {detailQuery.data.article_id}</span>
                <span>Revision ID: {detailQuery.data.revision_id}</span>
                <span>Reviewer: {detailQuery.data.reviewer_id || "—"}</span>
                <span>Section: {detailQuery.data.section_id || "—"}</span>
                <span>Block: {detailQuery.data.block_id || "—"}</span>
                <span>Created: {formatDate(detailQuery.data.created_at)}</span>
              </div>
            </DetailSection>

            <DetailSection
              title="All reviews for this article"
              description="Other reviews tied to the same article."
            >
              {listQuery.isLoading ? (
                <Typography variant="body2" className="text-brown-600">
                  Loading article reviews…
                </Typography>
              ) : listQuery.isError ? (
                <Typography variant="body2" className="text-terracotta">
                  {listQuery.error instanceof Error
                    ? listQuery.error.message
                    : "Unable to load article reviews."}
                </Typography>
              ) : Array.isArray(listQuery.data) && listQuery.data.length > 0 ? (
                <ArticleReviewList
                  items={listQuery.data}
                  showArticleId={false}
                />
              ) : (
                <Typography variant="body2" className="text-brown-600">
                  No other reviews for this article.
                </Typography>
              )}
            </DetailSection>
          </div>
        ) : (
          <Typography variant="body2" className="text-brown-600">
            Review data was not found.
          </Typography>
        )}
      </div>
    </AdminOnly>
  );
}
